const STATUS_COMMANDS = new Map([
  [":new", "new"],
  [":moved-in", "moved-in"],
  [":comeback", "comeback"],
  [":repriced", "price_change"],
  [":changed", "changed"],
  [":unchanged", "unchanged"],
]);

const CREATOR_NUMERIC_FIELDS = new Map([
  ["templates", "totalTemplates"],
  ["templatesdelta", "totalTemplatesDelta1d"],
  ["components", "totalComponents"],
  ["componentsdelta", "totalComponentsDelta1d"],
  ["plugins", "totalPlugins"],
  ["pluginsdelta", "totalPluginsDelta1d"],
  ["vectors", "totalVectors"],
  ["vectorsdelta", "totalVectorsDelta1d"],
  ["tutorials", "totalTutorials"],
  ["tutorialsdelta", "totalTutorialsDelta1d"],
  ["total", "totalItems"],
  ["totaldelta", "totalItemsDelta1d"],
]);

const NUMERIC_FIELDS = new Set([
  "delta",
  "rank",
  "price",
  "top%",
  ...CREATOR_NUMERIC_FIELDS.keys(),
]);

function parseNumericValue(value) {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;
  const number = Number(normalized.replace(/%$/, ""));
  return Number.isFinite(number) ? number : null;
}

function parseNumericCommand(token) {
  const match = token.match(/^([a-z%]+):(>=|<=|>|<|=)?(.+)$/i);
  if (!match) return null;
  const field = match[1].toLowerCase();
  if (!NUMERIC_FIELDS.has(field)) return null;
  let op = match[2] || "=";
  const rawValue = match[3].trim().toLowerCase();
  if (rawValue.startsWith("=") && op === "=") {
    op = "=";
  }
  const valueText = rawValue.startsWith("=") ? rawValue.slice(1) : rawValue;

  if (field === "price" && (valueText === "free" || valueText === "paid")) {
    return { kind: "numeric", field, value: valueText };
  }

  const value = parseNumericValue(valueText);
  if (value === null) return null;
  return { kind: "numeric", field, op, value };
}

export function parseSearchCommands(search = "") {
  const commands = [];
  const textTokens = [];
  const tokens = String(search ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  for (const token of tokens) {
    const normalizedToken = token.toLowerCase();
    if (STATUS_COMMANDS.has(normalizedToken)) {
      commands.push({ kind: "status", value: STATUS_COMMANDS.get(normalizedToken) });
      continue;
    }

    const numericCommand = parseNumericCommand(normalizedToken);
    if (numericCommand) {
      commands.push(numericCommand);
      continue;
    }

    textTokens.push(token);
  }

  return {
    textQuery: textTokens.join(" "),
    commands,
    errors: [],
  };
}

function getContextEntry(item, context) {
  const entry = item?.ranks?.[context];
  return Array.isArray(entry) ? entry : null;
}

function getNumericDelta(change) {
  return typeof change === "number" && Number.isFinite(change) ? change : null;
}

function getPriceNumber(price) {
  if (
    String(price ?? "")
      .trim()
      .toLowerCase() === "free"
  )
    return 0;
  const parsed = Number(
    String(price ?? "")
      .replace(/[$,]/g, "")
      .trim(),
  );
  return Number.isFinite(parsed) ? parsed : null;
}

function compareNumber(value, op, target) {
  if (!Number.isFinite(value)) return false;
  if (op === ">") return value > target;
  if (op === ">=") return value >= target;
  if (op === "<") return value < target;
  if (op === "<=") return value <= target;
  return value === target;
}

function matchesStatus(change, status) {
  if (status === "changed") {
    if (typeof change === "number") return change !== 0;
    return Boolean(change);
  }
  if (status === "unchanged") return change === 0;
  return change === status;
}

function getCommandNumber(item, command, entry, topPercentTotal) {
  const creatorStatsField = CREATOR_NUMERIC_FIELDS.get(command.field);
  if (creatorStatsField) return Number(item?.creatorStats?.[creatorStatsField]);
  if (command.field === "delta") return getNumericDelta(entry?.[1]);
  if (command.field === "rank") return Number(entry?.[0]);
  if (command.field === "price") return getPriceNumber(item?.price);
  if (command.field === "top%") {
    const rank = Number(entry?.[0]);
    const total = Number(topPercentTotal);
    if (!Number.isFinite(rank) || !Number.isFinite(total) || total <= 0) return null;
    return (rank / total) * 100;
  }
  return null;
}

function matchesNumericCommand(item, command, entry, topPercentTotal) {
  if (command.field === "price" && command.value === "free") return getPriceNumber(item?.price) === 0;
  if (command.field === "price" && command.value === "paid") {
    const price = getPriceNumber(item?.price);
    return price !== null && price > 0;
  }

  const value = getCommandNumber(item, command, entry, topPercentTotal);
  return compareNumber(value, command.op || "=", command.value);
}

export function itemMatchesSearchCommands(item, commands = [], options = {}) {
  if (!commands.length) return true;
  const entry = getContextEntry(item, options.context);

  for (const command of commands) {
    const usesCreatorStats = command.kind === "numeric" && CREATOR_NUMERIC_FIELDS.has(command.field);
    if (!usesCreatorStats && !entry) return false;
    if (command.kind === "status" && !matchesStatus(entry[1], command.value)) return false;
    if (command.kind === "numeric" && !matchesNumericCommand(item, command, entry, options.topPercentTotal)) {
      return false;
    }
  }

  return true;
}
